import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FontAwesome from 'utils/FA';

import '../styles/BulkActions.scss';

const BulkAction = ({
  children,
  depth,
  disabled,
  hideModal,
  icon,
  iconComponent: IconComponent,
  iconName,
  menuTitle,
  onClick,
  search,
  filterType,
  filterContactIds,
  showModal,
  title,
  type,
  soloButton
}) => {
  const secondaryDropdown = (React.Children.count(children)) > 0 && (depth > 1);
  const dropdownToggleClass = secondaryDropdown ? 'dropdown-item bulk-submenu-hover-color' : 'dropdown-toggle';
  const dropdownMenuClass = secondaryDropdown ? 'bulk-dropdown-submenu' : 'bulk-action-dropdown';

  const anchorAttrs = React.Children.count(children) > 0
    ? { className: classnames(dropdownToggleClass, { btn: soloButton, disabled }), 'data-bs-toggle': 'dropdown' }
    : { className: classnames('dropdown-item', { disabled }) };

  const Icon = () => {
    if (IconComponent) {
      return <IconComponent />;
    }

    return <FontAwesome icon={icon} name={iconName} postNbsp fixedWidth />;
  };

  return (
    <MenuItemWrapper disabled={disabled} soloButton={soloButton} type={type} secondaryDropdown={secondaryDropdown}>
      <a {...anchorAttrs} onClick={!disabled ? onClick : null} role='button'>
        {(depth !== 1 || iconName) && <Icon />}
        {title}
      </a>

      {
        React.Children.count(children) > 0 && (
          <ul className={`dropdown-menu bulk-dropdown-menu ${dropdownMenuClass}`}>
            {
              menuTitle && <li><div className='dropdown-header'>{menuTitle}</div></li>
            }
            {
              React.Children.map(children, (child) => child && React.cloneElement(child, {
                depth: depth + 1,
                search,
                filterType,
                filterContactIds,
                hideModal,
                showModal
              }))
            }
          </ul>
        )
      }
    </MenuItemWrapper>
  );
};
BulkAction.propTypes = {
  /** Child bulk actions */
  children: PropTypes.node,

  /** The depth in the bulk action tree that this action lives at */
  depth: PropTypes.number,

  /** If the bulk action is disabled */
  disabled: PropTypes.bool,

  /** Hides a modal */
  hideModal: PropTypes.func,

  /** Icon shown next to the bulk action */
  icon: PropTypes.shape({
    iconName: PropTypes.string
  }),

  /** Icon shown next to the bulk action if retrieving by name */
  iconName: PropTypes.string,

  /** Custom icon component. If used, icon and iconName props are ignored. */
  iconComponent: PropTypes.func,

  /** If there are children, the header of the child menu */
  menuTitle: PropTypes.string,

  /** The event that takes place when the bulk action is executed */
  onClick: PropTypes.func,

  /** The search we want to run our bulk action against */
  search: PropTypes.arrayOf(
    PropTypes.shape({
      criterion: PropTypes.string,
      modifier: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string)
    })
  ),

  /**
   * The type of search filter to apply with filterContactIds. Possible values: default, include, exclude
   *
   * include: apply only to filterContactIds
   * exclude: apply to search result _except_ filterContactIds
   * default: use search result without ID filters
   */
  filterType: PropTypes.string,

  /**
   * A List of Contact IDs to filter search against
   */
  filterContactIds: PropTypes.arrayOf(PropTypes.string),

  /** Shows a modal */
  showModal: PropTypes.func,

  /** The text displayed for the bulk action */
  title: PropTypes.string.isRequired,

  /** CSS class to add indicating type */
  type: PropTypes.string,

  /** Bulk action will not be within a list of bulkactions and needs to be its own button */
  soloButton: PropTypes.bool
};

BulkAction.defaultProps = {
  children: [],
  depth: 1,
  disabled: false,
  menuTitle: undefined,
  search: [],
  filterType: '',
  filterContactIds: [],
  soloButton: false,
  type: '',
  icon: undefined,
  iconName: undefined,

  hideModal: () => false,
  onClick: () => false,
  showModal: () => false
};

const MenuItemWrapper = (
  {
    disabled,
    type,
    soloButton,
    children,
    secondaryDropdown
  }
) => (soloButton ? <React.Fragment>{children}</React.Fragment>
  : (
    <li className={classnames(
      'bulk-action',
      'dropdown',
      secondaryDropdown ? 'bulk-submenu-hover-color' : '',
      type,
      { disabled }
    )}
    >
      {children}
    </li>
  ));

MenuItemWrapper.propTypes = {
  /** Child bulk actions */
  children: PropTypes.node,

  /** If the bulk action is disabled */
  disabled: PropTypes.bool,

  /** CSS class to add indicating type */
  type: PropTypes.string,

  /** Indicates we've already opened one dropdown so we're going to have to go to the side */
  secondaryDropdown: PropTypes.bool,

  /** Bulk action will not be within a list of bulkactions and needs to be its own button */
  soloButton: PropTypes.bool
};

MenuItemWrapper.defaultProps = {
  children: [],
  disabled: false,
  secondaryDropdown: false,
  soloButton: false,
  type: ''
};

export default BulkAction;
